/* eslint-disable typescript-extended-rules/file-naming-convention */
import {
	BeneficiaryReconciliationMappingBenefitKind,
	BeneficiaryReconciliationMappingCanonicalField,
	BeneficiaryReconciliationMappingConfirmation,
	BeneficiaryReconciliationMappingFieldSelection,
	BeneficiaryReconciliationMappingFieldState,
	BeneficiaryReconciliationMappingSourceAnalysis,
	BeneficiaryReconciliationMappingSourceConfig,
	BeneficiaryReconciliationMappingSourceUsage,
	BeneficiaryReconciliationMappingTitularityKind,
} from '../../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation.model';

import {
	BENEFICIARY_RECONCILIATION_MAPPING_CONGENER_FIELDS,
	BENEFICIARY_RECONCILIATION_MAPPING_CONJUGATED_FIELDS,
	BENEFICIARY_RECONCILIATION_MAPPING_DENTAL_FIELDS,
	BENEFICIARY_RECONCILIATION_MAPPING_GENERAL_REQUIRED_FIELDS,
	BENEFICIARY_RECONCILIATION_MAPPING_HEALTH_FIELDS,
	getMappingFieldLabel,
	mappingFieldAllowsMissingFromSource,
	mappingFieldAllowsNotApplicable,
} from './beneficiary-reconciliation-mapping-modal.constants';

export const MAPPING_COLUMN_OPTION_PREFIX = 'COLUMN::';
export const MAPPING_SPECIAL_OPTION_PREFIX = 'SPECIAL::';

type MappingFieldSelectionMap = Partial<
	Record<
		BeneficiaryReconciliationMappingCanonicalField,
		BeneficiaryReconciliationMappingFieldSelection
	>
>;

export interface MappingSourceDraft {
	benefitKind: BeneficiaryReconciliationMappingBenefitKind | null;
	fieldValues: Partial<
		Record<BeneficiaryReconciliationMappingCanonicalField, unknown>
	>;
	source: BeneficiaryReconciliationMappingSourceAnalysis;
	titularityKind: BeneficiaryReconciliationMappingTitularityKind | null;
	usage: BeneficiaryReconciliationMappingSourceUsage;
	visibleFields?: BeneficiaryReconciliationMappingCanonicalField[];
}

export function getMappingColumnOptionValue(columnName: string): string {
	return `${MAPPING_COLUMN_OPTION_PREFIX}${columnName}`;
}

export function getMappingSpecialOptionValue(
	state: BeneficiaryReconciliationMappingFieldState,
): string {
	return `${MAPPING_SPECIAL_OPTION_PREFIX}${state}`;
}

export function parseMappingFieldSelection(
	value: unknown,
): BeneficiaryReconciliationMappingFieldSelection {
	const rawValue = typeof value === 'string' ? value : '';

	if (rawValue.startsWith(MAPPING_COLUMN_OPTION_PREFIX)) {
		return {
			column: rawValue.slice(MAPPING_COLUMN_OPTION_PREFIX.length),
			state: BeneficiaryReconciliationMappingFieldState.COLUMN,
		};
	}

	if (rawValue.startsWith(MAPPING_SPECIAL_OPTION_PREFIX)) {
		return {
			state: rawValue.slice(
				MAPPING_SPECIAL_OPTION_PREFIX.length,
			) as BeneficiaryReconciliationMappingFieldState,
		};
	}

	return {
		state: BeneficiaryReconciliationMappingFieldState.PENDING,
	};
}

function buildFieldMap(draft: MappingSourceDraft): MappingFieldSelectionMap {
	const visibleFieldSet = new Set(draft.visibleFields);

	return Object.entries(draft.fieldValues).reduce<MappingFieldSelectionMap>(
		(fields, [rawField, rawValue]) => {
			const field = rawField as BeneficiaryReconciliationMappingCanonicalField;
			const selection = parseMappingFieldSelection(rawValue);

			if (
				(draft.visibleFields === undefined || visibleFieldSet.has(field)) &&
				selection.state !== BeneficiaryReconciliationMappingFieldState.PENDING
			) {
				fields[field] = selection;
			}

			return fields;
		},
		{},
	);
}

function buildSourceConfig(
	draft: MappingSourceDraft,
): BeneficiaryReconciliationMappingSourceConfig {
	const isIgnored =
		draft.usage === BeneficiaryReconciliationMappingSourceUsage.IGNORE;

	return {
		benefitKind:
			draft.benefitKind ?? BeneficiaryReconciliationMappingBenefitKind.HEALTH,
		fields: isIgnored ? {} : buildFieldMap(draft),
		fileIndex: draft.source.fileIndex,
		fileName: draft.source.fileName,
		sheetName: draft.source.sheetName,
		sourceId: draft.source.sourceId,
		titularityKind:
			draft.titularityKind ??
			BeneficiaryReconciliationMappingTitularityKind.HOLDERS,
		usage: draft.usage,
	};
}

export function buildMappingConfirmation(
	drafts: MappingSourceDraft[],
): BeneficiaryReconciliationMappingConfirmation {
	return {
		sources: drafts.map((draft) => buildSourceConfig(draft)),
	};
}

function fieldSelectionToValidate(
	draft: MappingSourceDraft,
	field: BeneficiaryReconciliationMappingCanonicalField,
): BeneficiaryReconciliationMappingFieldSelection {
	return parseMappingFieldSelection(draft.fieldValues[field]);
}

function getMappedOptionalFields(
	draft: MappingSourceDraft,
	requiredFields: BeneficiaryReconciliationMappingCanonicalField[],
): BeneficiaryReconciliationMappingCanonicalField[] {
	const requiredFieldSet = new Set(requiredFields);
	const visibleFieldSet = new Set(draft.visibleFields);

	return Object.entries(draft.fieldValues)
		.filter(([rawField, rawValue]) => {
			const field = rawField as BeneficiaryReconciliationMappingCanonicalField;
			const selection = parseMappingFieldSelection(rawValue);

			return (
				(draft.visibleFields === undefined || visibleFieldSet.has(field)) &&
				!requiredFieldSet.has(field) &&
				selection.state !== BeneficiaryReconciliationMappingFieldState.PENDING
			);
		})
		.map(([field]) => field as BeneficiaryReconciliationMappingCanonicalField);
}

function getPrincipalBenefitRequiredFields(
	benefitKind: BeneficiaryReconciliationMappingBenefitKind | null,
): BeneficiaryReconciliationMappingCanonicalField[] {
	switch (benefitKind) {
		case BeneficiaryReconciliationMappingBenefitKind.HEALTH:
			return [...BENEFICIARY_RECONCILIATION_MAPPING_HEALTH_FIELDS];
		case BeneficiaryReconciliationMappingBenefitKind.DENTAL:
			return [...BENEFICIARY_RECONCILIATION_MAPPING_DENTAL_FIELDS];
		case BeneficiaryReconciliationMappingBenefitKind.HEALTH_DENTAL:
			return [
				...BENEFICIARY_RECONCILIATION_MAPPING_HEALTH_FIELDS,
				...BENEFICIARY_RECONCILIATION_MAPPING_DENTAL_FIELDS,
			];
		case BeneficiaryReconciliationMappingBenefitKind.CONJUGATED:
			return [...BENEFICIARY_RECONCILIATION_MAPPING_CONJUGATED_FIELDS];
		default:
			return [];
	}
}

function getPrincipalBenefitVisibleFields(
	benefitKind: BeneficiaryReconciliationMappingBenefitKind | null,
): BeneficiaryReconciliationMappingCanonicalField[] {
	return getPrincipalBenefitRequiredFields(benefitKind);
}

function getSourceLabel(
	source: BeneficiaryReconciliationMappingSourceAnalysis,
): string {
	return `${source.fileName} / ${source.sheetName}`;
}

function getTitularityRequiredFields(
	titularityKind: BeneficiaryReconciliationMappingTitularityKind | null,
): BeneficiaryReconciliationMappingCanonicalField[] {
	switch (titularityKind) {
		case BeneficiaryReconciliationMappingTitularityKind.HOLDERS:
			return [];
		case BeneficiaryReconciliationMappingTitularityKind.DEPENDENTS:
			return [];
		case BeneficiaryReconciliationMappingTitularityKind.HOLDERS_DEPENDENTS:
			return [BeneficiaryReconciliationMappingCanonicalField.TITULARITY];
		default:
			return [];
	}
}

function getTitularityVisibleFields(
	titularityKind: BeneficiaryReconciliationMappingTitularityKind | null,
): BeneficiaryReconciliationMappingCanonicalField[] {
	switch (titularityKind) {
		case BeneficiaryReconciliationMappingTitularityKind.HOLDERS:
			return [];
		case BeneficiaryReconciliationMappingTitularityKind.DEPENDENTS:
			return [
				BeneficiaryReconciliationMappingCanonicalField.HOLDER_CPF,
				BeneficiaryReconciliationMappingCanonicalField.PARENTESCO,
			];
		case BeneficiaryReconciliationMappingTitularityKind.HOLDERS_DEPENDENTS:
			return [
				BeneficiaryReconciliationMappingCanonicalField.TITULARITY,
				BeneficiaryReconciliationMappingCanonicalField.HOLDER_CPF,
				BeneficiaryReconciliationMappingCanonicalField.PARENTESCO,
			];
		default:
			return [];
	}
}

function getVariantDecisionFields(): BeneficiaryReconciliationMappingCanonicalField[] {
	return [...BENEFICIARY_RECONCILIATION_MAPPING_CONGENER_FIELDS];
}

export function getMappingRequiredFields(
	benefitKind: BeneficiaryReconciliationMappingBenefitKind | null,
	titularityKind: BeneficiaryReconciliationMappingTitularityKind | null,
): BeneficiaryReconciliationMappingCanonicalField[] {
	return [
		...BENEFICIARY_RECONCILIATION_MAPPING_GENERAL_REQUIRED_FIELDS,
		...getTitularityRequiredFields(titularityKind),
		...getPrincipalBenefitRequiredFields(benefitKind),
		...getVariantDecisionFields(),
	];
}

export function getVisibleMappingFields(
	benefitKind: BeneficiaryReconciliationMappingBenefitKind | null,
	titularityKind: BeneficiaryReconciliationMappingTitularityKind | null,
): BeneficiaryReconciliationMappingCanonicalField[] {
	return [
		...BENEFICIARY_RECONCILIATION_MAPPING_GENERAL_REQUIRED_FIELDS,
		...getTitularityVisibleFields(titularityKind),
		...getPrincipalBenefitVisibleFields(benefitKind),
		...getVariantDecisionFields(),
	];
}

function hasAvailableColumn(
	source: BeneficiaryReconciliationMappingSourceAnalysis,
	column: string | undefined,
): boolean {
	return Boolean(
		column &&
			source.columns.some((sourceColumn) => sourceColumn.sourceName === column),
	);
}

function isBenefitKindValid(
	benefitKind: BeneficiaryReconciliationMappingBenefitKind | null,
): benefitKind is BeneficiaryReconciliationMappingBenefitKind {
	return Object.values(BeneficiaryReconciliationMappingBenefitKind).includes(
		benefitKind as BeneficiaryReconciliationMappingBenefitKind,
	);
}

function isTitularityKindValid(
	titularityKind: BeneficiaryReconciliationMappingTitularityKind | null,
): titularityKind is BeneficiaryReconciliationMappingTitularityKind {
	return Object.values(BeneficiaryReconciliationMappingTitularityKind).includes(
		titularityKind as BeneficiaryReconciliationMappingTitularityKind,
	);
}

function validateMappedOptionalField(
	draft: MappingSourceDraft,
	field: BeneficiaryReconciliationMappingCanonicalField,
): string[] {
	const selection = fieldSelectionToValidate(draft, field);

	if (selection.state === BeneficiaryReconciliationMappingFieldState.COLUMN) {
		return hasAvailableColumn(draft.source, selection.column)
			? []
			: [
					`${getSourceLabel(draft.source)}: coluna de ${getMappingFieldLabel(
						field,
					)} não encontrada.`,
				];
	}

	if (
		selection.state ===
			BeneficiaryReconciliationMappingFieldState.MISSING_FROM_SOURCE &&
		mappingFieldAllowsMissingFromSource(field, draft.benefitKind)
	) {
		return [];
	}

	if (
		selection.state ===
			BeneficiaryReconciliationMappingFieldState.NOT_APPLICABLE &&
		mappingFieldAllowsNotApplicable(field)
	) {
		return [];
	}

	return [
		`${getSourceLabel(draft.source)}: decisão inválida para ${getMappingFieldLabel(
			field,
		)}.`,
	];
}

function validateRequiredDecisionField(
	draft: MappingSourceDraft,
	field: BeneficiaryReconciliationMappingCanonicalField,
): string[] {
	const selection = fieldSelectionToValidate(draft, field);

	if (selection.state === BeneficiaryReconciliationMappingFieldState.PENDING) {
		return [
			`${getSourceLabel(draft.source)}: informe ${getMappingFieldLabel(field)}.`,
		];
	}

	if (selection.state === BeneficiaryReconciliationMappingFieldState.COLUMN) {
		return hasAvailableColumn(draft.source, selection.column)
			? []
			: [
					`${getSourceLabel(draft.source)}: coluna de ${getMappingFieldLabel(
						field,
					)} não encontrada.`,
				];
	}

	if (
		selection.state ===
			BeneficiaryReconciliationMappingFieldState.MISSING_FROM_SOURCE &&
		mappingFieldAllowsMissingFromSource(field, draft.benefitKind)
	) {
		return [];
	}

	if (
		selection.state ===
			BeneficiaryReconciliationMappingFieldState.NOT_APPLICABLE &&
		mappingFieldAllowsNotApplicable(field)
	) {
		return [];
	}

	return [
		`${getSourceLabel(draft.source)}: decisão inválida para ${getMappingFieldLabel(
			field,
		)}.`,
	];
}

function validateRequiredGeneralColumnField(
	draft: MappingSourceDraft,
	field: BeneficiaryReconciliationMappingCanonicalField,
): string[] {
	const selection = fieldSelectionToValidate(draft, field);
	const fieldLabel = getMappingFieldLabel(field);

	if (selection.state === BeneficiaryReconciliationMappingFieldState.PENDING) {
		return [`${getSourceLabel(draft.source)}: informe ${fieldLabel}.`];
	}

	if (selection.state !== BeneficiaryReconciliationMappingFieldState.COLUMN) {
		return [
			`${getSourceLabel(draft.source)}: ${fieldLabel} deve selecionar uma coluna.`,
		];
	}

	return hasAvailableColumn(draft.source, selection.column)
		? []
		: [
				`${getSourceLabel(draft.source)}: coluna de ${fieldLabel} não encontrada.`,
			];
}

function validateUsedSourceDraft(draft: MappingSourceDraft): string[] {
	const sourceLabel = getSourceLabel(draft.source);
	const errors: string[] = [];

	if (!isBenefitKindValid(draft.benefitKind)) {
		errors.push(`${sourceLabel}: selecione um tipo de benefício válido.`);
	}

	if (!isTitularityKindValid(draft.titularityKind)) {
		errors.push(`${sourceLabel}: selecione um tipo de titularidade válido.`);
	}

	if (errors.length > 0) {
		return errors;
	}

	const requiredFields = getMappingRequiredFields(
		draft.benefitKind,
		draft.titularityKind,
	);

	BENEFICIARY_RECONCILIATION_MAPPING_GENERAL_REQUIRED_FIELDS.forEach(
		(field) => {
			errors.push(...validateRequiredGeneralColumnField(draft, field));
		},
	);
	getTitularityRequiredFields(draft.titularityKind).forEach((field) => {
		errors.push(...validateRequiredGeneralColumnField(draft, field));
	});
	getPrincipalBenefitRequiredFields(draft.benefitKind).forEach((field) => {
		errors.push(...validateRequiredDecisionField(draft, field));
	});
	getVariantDecisionFields().forEach((field) => {
		errors.push(...validateRequiredDecisionField(draft, field));
	});
	getMappedOptionalFields(draft, requiredFields).forEach((field) => {
		errors.push(...validateMappedOptionalField(draft, field));
	});

	return errors;
}

export function buildMappingValidationErrors(
	drafts: MappingSourceDraft[],
): string[] {
	return drafts.reduce<string[]>((errors, draft) => {
		if (draft.usage === BeneficiaryReconciliationMappingSourceUsage.IGNORE) {
			return errors;
		}

		errors.push(...validateUsedSourceDraft(draft));

		return errors;
	}, []);
}
